/**
 * Character art QC — shared by Art Lab Contract + Vitest.
 * SSOT: docs/testing/Game_Proto_아트랩_하네스.md (Contract PASS = complete)
 */

import type { ArtPack } from "../../art/types";
import { bakePixels } from "../../art/bake-pixels";
import {
  countIsolatedPixels,
  frameHeight,
  hammingAfterBestYAlign,
  measureProportionQuality,
  measureRectRead,
  measureToneBands,
  solidCount,
} from "../../art/qc";
import type { AttackFxTables } from "../../vfx/attack-fx";
import { resolveAttackFx } from "../../vfx/attack-fx";
import type { GripPack } from "../../view/unit-rig/grip-resolve";
import type { RigTables } from "../../view/unit-rig/unit-rig-math";
import { resolveRigConfig } from "../../view/unit-rig/unit-rig-math";

export { hammingAfterBestYAlign } from "../../art/qc";

export type QcSeverity = "pass" | "warn" | "fail";

export type QcItem = {
  id: string;
  severity: QcSeverity;
  detail: string;
};

export type CharacterQcReport = {
  packId: string;
  characterId: string;
  overall: QcSeverity;
  items: QcItem[];
};

export type CharacterQcInput = {
  packId: string;
  characterId: string;
  pack: ArtPack;
  grips?: GripPack | null;
  rig?: RigTables | null;
  attackFx?: AttackFxTables | null;
  /** Known VFX preset ids (from presets.json). */
  presetIds?: ReadonlySet<string> | readonly string[];
  /** When true, apply Dropfront humanoid proportion hard gate. */
  humanoidProportion?: boolean;
};

const ANTI_SHIFTY_MIN = 80;
/** Frontline attack choreography (Duelyst hybrid). */
const ATTACK_FRAMES_FRONTLINE = 6;
const POSE_IDLE_MIN = 40;
const POSE_CONSEC_MIN = 2;
const POSE_PHASE_JUMP_MIN = 40;
/** Body contact accents outside idle bbox — smear belongs on FX sprites. */
const BODY_SMEAR_MAX = 8;
/** FFA/Frontline: opaque contact pixels outside idle±pad (body-keyed smear). */
const BODY_OPAQUE_SMEAR_MAX = 24;
const ACCENT_KEYS = new Set(["3", "4", "5", "6", "h", "g"]);
const FX_RIM_DIFF_MIN = 8;
const FX_DISTINCT_MIN = 30;
/**
 * Perceptual readability (src/art/qc/readability.ts). Calibrated 2026-08 over
 * all packs: good bodies (dropfront roles, ffa) measure run≤11 block≤4 iso=0;
 * procedural-block bodies (legacy frontline/raid) measure run≥17 block≥6.
 */
const RECT_RUN_MAX = 12;
const RECT_BLOCK_MAX = 4;
const TONE_BAND_MIN = 2;
const ISOLATED_MAX = 6;
const FFA_COMBAT_QC = new Set([
  "master",
  "hero",
  "raider",
  "guardian",
  "soldier",
  "chaser",
]);

type ContinuityLimits = { heightMax: number; solidMax: number };

function continuityLimits(packId: string): ContinuityLimits {
  // Frontline / Raid titans are denser across dirs than Dropfront infantry.
  if (packId === "frontline" || packId === "raid" || packId === "infinite-war") {
    return { heightMax: 6, solidMax: 550 };
  }
  return { heightMax: 4, solidMax: 72 };
}

// All packs hold the hard readability bar (raid_boss was the last legacy body).

function worst(a: QcSeverity, b: QcSeverity): QcSeverity {
  const rank = { pass: 0, warn: 1, fail: 2 } as const;
  return rank[a] >= rank[b] ? a : b;
}

function overallOf(items: QcItem[]): QcSeverity {
  return items.reduce<QcSeverity>(
    (acc, it) => worst(acc, it.severity),
    "pass",
  );
}

function opaqueNear(
  rows: string[],
  gx: number,
  gy: number,
  radius: number,
): number {
  let n = 0;
  for (let y = gy - radius; y <= gy + radius; y++) {
    for (let x = gx - radius; x <= gx + radius; x++) {
      if (y < 0 || y >= rows.length || x < 0 || x >= (rows[0]?.length ?? 0)) {
        continue;
      }
      if (rows[y]![x] !== ".") n++;
    }
  }
  return n;
}

function uniqueOpaqueKeys(rows: string[]): number {
  const keys = new Set<string>();
  for (const row of rows) {
    for (const ch of row) {
      if (ch !== ".") keys.add(ch);
    }
  }
  return keys.size;
}

/** Opaque silhouette Hamming (ignore color; occupancy only). */
function silhouetteHamming(a: string[], b: string[]): number {
  let d = 0;
  for (let y = 0; y < 64; y++) {
    for (let x = 0; x < 64; x++) {
      const oa = a[y]![x]! !== ".";
      const ob = b[y]![x]! !== ".";
      if (oa !== ob) d++;
    }
  }
  return d;
}

function opaqueBbox(rows: string[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  let minX = 64;
  let minY = 64;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < rows.length; y++) {
    const row = rows[y]!;
    for (let x = 0; x < row.length; x++) {
      if (row[x] === ".") continue;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < 0) return { minX: 0, minY: 0, maxX: 63, maxY: 63 };
  return { minX, minY, maxX, maxY };
}

function accentOutsideBbox(
  rows: string[],
  bbox: { minX: number; minY: number; maxX: number; maxY: number },
): number {
  let n = 0;
  for (let y = 0; y < rows.length; y++) {
    const row = rows[y]!;
    for (let x = 0; x < row.length; x++) {
      const ch = row[x]!;
      if (!ACCENT_KEYS.has(ch)) continue;
      const inside =
        x >= bbox.minX &&
        x <= bbox.maxX &&
        y >= bbox.minY &&
        y <= bbox.maxY;
      if (!inside) n++;
    }
  }
  return n;
}

function opaqueOutsideBbox(
  rows: string[],
  bbox: { minX: number; minY: number; maxX: number; maxY: number },
): number {
  let n = 0;
  for (let y = 0; y < rows.length; y++) {
    const row = rows[y]!;
    for (let x = 0; x < row.length; x++) {
      if (row[x] === ".") continue;
      const inside =
        x >= bbox.minX &&
        x <= bbox.maxX &&
        y >= bbox.minY &&
        y <= bbox.maxY;
      if (!inside) n++;
    }
  }
  return n;
}

function rimBakeDiff(
  pack: ArtPack,
  spriteId: string,
): number {
  const spr = pack.sprites[spriteId];
  if (!spr) return 0;
  const ally = pack.affiliation.ally;
  const enemy =
    pack.affiliation.enemy ??
    (pack.affiliation as { enemy1?: { rim?: string } }).enemy1;
  if (!ally?.rim || !enemy?.rim) return 0;
  const a = bakePixels(spr, pack.palette, ally);
  const b = bakePixels(spr, pack.palette, enemy);
  let diff = 0;
  for (let i = 0; i < a.rgba.length; i += 4) {
    if (
      a.rgba[i] !== b.rgba[i] ||
      a.rgba[i + 1] !== b.rgba[i + 1] ||
      a.rgba[i + 2] !== b.rgba[i + 2]
    ) {
      diff++;
    }
  }
  return diff;
}

function presetSet(
  ids: CharacterQcInput["presetIds"],
): ReadonlySet<string> | null {
  if (!ids) return null;
  return ids instanceof Set ? ids : new Set(ids);
}

/**
 * Run combat-character QC for one id.
 * Returns fail items for missing authored dirs (caller may fall back to kind gates).
 */
export function runCharacterQc(input: CharacterQcInput): CharacterQcReport {
  const {
    packId,
    characterId,
    pack,
    grips,
    rig,
    attackFx,
    humanoidProportion = packId === "dropfront",
  } = input;
  const presets = presetSet(input.presetIds);
  const items: QcItem[] = [];
  const anim = pack.anims[characterId];
  const dirs = anim?.dirs;

  // ── dirs ──────────────────────────────────────────────────────────────
  if (!dirs?.e || !dirs?.n || !dirs?.s) {
    items.push({
      id: "dirs",
      severity: "fail",
      detail: `missing authored dirs (need e/n/s; have ${dirs ? Object.keys(dirs).join(",") : "none"})`,
    });
    return {
      packId,
      characterId,
      overall: "fail",
      items,
    };
  }
  const hasW = Boolean(dirs.w);
  items.push({
    id: "dirs",
    severity: "pass",
    detail: hasW ? "e/n/s + w" : "e/n/s (w synthesized at load)",
  });

  const e = dirs.e;
  const n = dirs.n;
  const s = dirs.s;
  const eIdle = e.idle.frames[0]!;
  const nIdle = n.idle.frames[0]!;
  const sIdle = s.idle.frames[0]!;

  // ── attack_frames ─────────────────────────────────────────────────────
  const atkMin =
    packId === "frontline" || packId === "infinite-war"
      ? ATTACK_FRAMES_FRONTLINE
      : 3;
  const atkLens = {
    e: e.attack?.frames?.length ?? 0,
    n: n.attack?.frames?.length ?? 0,
    s: s.attack?.frames?.length ?? 0,
  };
  const attackOk =
    atkLens.e >= atkMin &&
    atkLens.n >= atkMin &&
    atkLens.s >= atkMin &&
    e.attack.frames[0]!.join("") !== e.idle.frames[0]!.join("") &&
    e.attack.frames[1]!.join("") !== e.attack.frames[0]!.join("");
  items.push({
    id: "attack_frames",
    severity: attackOk ? "pass" : "fail",
    detail: attackOk
      ? `attack e/n/s=${atkLens.e}/${atkLens.n}/${atkLens.s}, ≠idle (≥${atkMin})`
      : `need ≥${atkMin} frames/dir + attack≠idle (got e/n/s=${atkLens.e}/${atkLens.n}/${atkLens.s})`,
  });

  // ── attack choreography (Frontline) ───────────────────────────────────
  if (packId === "frontline" && atkLens.e >= 2) {
    const frames = e.attack.frames;
    const idleVs0 = silhouetteHamming(eIdle, frames[0]!);
    let minConsec = Number.POSITIVE_INFINITY;
    for (let i = 1; i < frames.length; i++) {
      minConsec = Math.min(
        minConsec,
        silhouetteHamming(frames[i - 1]!, frames[i]!),
      );
    }
    const contactIdx = Math.max(0, Math.floor(frames.length / 2) - 1);
    const phaseJump = silhouetteHamming(frames[0]!, frames[contactIdx]!);
    const poseOk =
      idleVs0 >= POSE_IDLE_MIN &&
      minConsec >= POSE_CONSEC_MIN &&
      phaseJump >= POSE_PHASE_JUMP_MIN;
    items.push({
      id: "attack_pose_delta",
      severity: poseOk ? "pass" : "fail",
      detail: `pose-only choreography idle↔f0=${idleVs0} (min ${POSE_IDLE_MIN}); minConsec=${minConsec === Number.POSITIVE_INFINITY ? 0 : minConsec} (min ${POSE_CONSEC_MIN}); ant→contact=${phaseJump} (min ${POSE_PHASE_JUMP_MIN}) — smear is on sprite_fx`,
    });
  }

  // ── walk_frames ───────────────────────────────────────────────────────
  const walkLen = e.walk?.frames?.length ?? 0;
  items.push({
    id: "walk_frames",
    severity: walkLen >= 2 ? "pass" : "fail",
    detail: `walk frames=${walkLen}`,
  });

  // ── anti_shifty ───────────────────────────────────────────────────────
  const hamN = hammingAfterBestYAlign(eIdle, nIdle);
  const hamS = hammingAfterBestYAlign(eIdle, sIdle);
  const antiOk = hamN >= ANTI_SHIFTY_MIN && hamS >= ANTI_SHIFTY_MIN;
  items.push({
    id: "anti_shifty",
    severity: antiOk ? "pass" : "fail",
    detail: `ham N=${hamN} S=${hamS} (min ${ANTI_SHIFTY_MIN})`,
  });

  // ── continuity ────────────────────────────────────────────────────────
  const lim = continuityLimits(packId);
  const he = frameHeight(eIdle);
  const hn = frameHeight(nIdle);
  const hs = frameHeight(sIdle);
  const se = solidCount(eIdle);
  const sn = solidCount(nIdle);
  const ss = solidCount(sIdle);
  const heightOk =
    Math.abs(he - hn) <= lim.heightMax && Math.abs(he - hs) <= lim.heightMax;
  const solidSpread = Math.max(se, sn, ss) - Math.min(se, sn, ss);
  const massOk = solidSpread <= lim.solidMax;
  let wIsFlipE = true;
  if (dirs.w?.idle?.frames?.[0]) {
    const e0 = eIdle[0] ?? "";
    const w0 = dirs.w.idle.frames[0][0] ?? "";
    wIsFlipE = w0 === [...e0].reverse().join("");
  }
  const contOk = heightOk && massOk && wIsFlipE;
  items.push({
    id: "continuity",
    severity: contOk ? "pass" : "fail",
    detail: `h ${he}/${hn}/${hs} Δ≤${lim.heightMax} ${heightOk ? "OK" : "FAIL"}; mass spread ${solidSpread}≤${lim.solidMax} ${massOk ? "OK" : "FAIL"}; W=flip(E) ${wIsFlipE ? "OK" : "FAIL"}`,
  });

  // ── proportion ────────────────────────────────────────────────────────
  if (humanoidProportion) {
    const prop = measureProportionQuality(eIdle);
    items.push({
      id: "proportion",
      severity: prop.ok ? "pass" : "fail",
      detail: prop.ok
        ? `keys=${prop.uniqueOpaqueKeys} solid=${prop.solidCount}`
        : prop.failReasons.join("; "),
    });
  } else {
    const keys = uniqueOpaqueKeys(eIdle);
    items.push({
      id: "proportion",
      severity: keys >= 4 ? "pass" : "warn",
      detail: `non-humanoid soft keys=${keys} (warn if <4)`,
    });
  }

  // ── rect_read / tone_bands / isolated_noise (perceptual readability) ──
  const readSev: QcSeverity = "fail";
  const seloutKey = pack.palette.ramps?.selout ?? "f";
  let worstRun = 0;
  let worstBlock = 0;
  let minBand = Number.POSITIVE_INFINITY;
  let maxIso = 0;
  for (const idle of [eIdle, nIdle, sIdle]) {
    const rr = measureRectRead(idle);
    worstRun = Math.max(worstRun, rr.maxRun);
    worstBlock = Math.max(worstBlock, rr.maxBlock);
    const tb = measureToneBands(idle, seloutKey);
    minBand = Math.min(minBand, ...tb.bands);
    maxIso = Math.max(maxIso, countIsolatedPixels(idle));
  }
  const rectOk = worstRun <= RECT_RUN_MAX && worstBlock <= RECT_BLOCK_MAX;
  items.push({
    id: "rect_read",
    severity: rectOk ? "pass" : readSev,
    detail: `flat-mass run=${worstRun} (max ${RECT_RUN_MAX}) block=${worstBlock} (max ${RECT_BLOCK_MAX}) — long single-key masses read as geometry`,
  });
  const toneOk = minBand >= TONE_BAND_MIN;
  items.push({
    id: "tone_bands",
    severity: toneOk ? "pass" : readSev,
    detail: `min tones per body third=${minBand} (min ${TONE_BAND_MIN}) — every third needs shading, no flat fill`,
  });
  items.push({
    id: "isolated_noise",
    severity: maxIso <= ISOLATED_MAX ? "pass" : "warn",
    detail: `isolated pixels=${maxIso} (max ${ISOLATED_MAX})`,
  });

  // ── grip_ssot ─────────────────────────────────────────────────────────
  const kit = grips?.byCharacter?.[characterId];
  if (!kit?.e || !kit?.n || !kit?.s) {
    items.push({
      id: "grip_ssot",
      severity: "fail",
      detail: "missing grips.byCharacter e/n/s",
    });
  } else {
    const atkOk =
      (kit.e.attack?.length ?? 0) >=
        (packId === "frontline" || packId === "infinite-war" ? 6 : 3) &&
      (kit.n.attack?.length ?? 0) >=
        (packId === "frontline" || packId === "infinite-war" ? 6 : 3) &&
      (kit.s.attack?.length ?? 0) >=
        (packId === "frontline" || packId === "infinite-war" ? 6 : 3);
    const idleOk =
      (kit.e.idle?.length ?? 0) >= 1 &&
      (kit.n.idle?.length ?? 0) >= 1 &&
      (kit.s.idle?.length ?? 0) >= 1;
    const walkOk =
      (kit.e.walk?.length ?? 0) >= 1 &&
      (kit.n.walk?.length ?? 0) >= 1 &&
      (kit.s.walk?.length ?? 0) >= 1;
    const gripAtkMin =
      packId === "frontline" || packId === "infinite-war" ? 6 : 3;
    items.push({
      id: "grip_ssot",
      severity: atkOk && idleOk && walkOk ? "pass" : "fail",
      detail: atkOk && idleOk && walkOk
        ? `grip e/n/s idle/walk/attack ok (atk≥${gripAtkMin})`
        : `grip clips incomplete (idle/walk/attack≥${gripAtkMin})`,
    });

    // ── grip_on_body ────────────────────────────────────────────────────
    const [gx, gy] = kit.e.idle[0]!;
    // r=1 is strict on-hand; r=2 allows muzzle/horn tips just outside silhouette.
    const near = opaqueNear(eIdle, gx, gy, 2);
    items.push({
      id: "grip_on_body",
      severity: near >= 1 ? "pass" : "fail",
      detail: `idle-e grip (${gx},${gy}) opaqueNear(r2)=${near}`,
    });

    // ── double_armament ─────────────────────────────────────────────────
    const rigCfg = resolveRigConfig(rig ?? undefined, characterId);
    const hasExplicitRig = Boolean(rig?.byRole?.[characterId]);
    const dense = opaqueNear(eIdle, gx, gy, 4);
    const longWeapon = (rigCfg.weaponLength ?? 0) >= 24;
    if (!rigCfg.showWeapon) {
      items.push({
        id: "double_armament",
        severity: "pass",
        detail: `body-owned armament (showWeapon=false, dense=${dense})`,
      });
    } else if (
      packId === "frontline" &&
      hasExplicitRig &&
      dense >= 20 &&
      longWeapon
    ) {
      items.push({
        id: "double_armament",
        severity: "fail",
        detail: `Frontline FAIL: showWeapon=true + body dense near grip=${dense}, weaponLength=${rigCfg.weaponLength} (${rigCfg.weaponShape}) — strip body weapon or set showWeapon:false`,
      });
    } else if (hasExplicitRig && dense >= 20 && longWeapon) {
      items.push({
        id: "double_armament",
        severity: "warn",
        detail: `body solid near grip=${dense}, weaponLength=${rigCfg.weaponLength} (${rigCfg.weaponShape}) — verify baked body weapon vs UnitRig`,
      });
    } else {
      items.push({
        id: "double_armament",
        severity: "pass",
        detail: hasExplicitRig
          ? `ok (dense=${dense}, length=${rigCfg.weaponLength})`
          : "no explicit rig.byRole",
      });
    }
  }

  // ── rig_kit ───────────────────────────────────────────────────────────
  if (!rig?.byRole?.[characterId]) {
    items.push({
      id: "rig_kit",
      severity: "fail",
      detail: "missing rig.byRole[characterId]",
    });
  } else {
    const cfg = resolveRigConfig(rig, characterId);
    const ok =
      Boolean(cfg.weaponShape) &&
      Boolean(cfg.attackStyle) &&
      (cfg.weaponLength ?? 0) > 0;
    items.push({
      id: "rig_kit",
      severity: ok ? "pass" : "fail",
      detail: `${cfg.weaponShape}/${cfg.attackStyle} length=${cfg.weaponLength}`,
    });
  }

  // ── attack_fx ─────────────────────────────────────────────────────────
  if (!attackFx?.byRole?.[characterId]) {
    items.push({
      id: "attack_fx",
      severity: "fail",
      detail: "missing attackFx.byRole[characterId]",
    });
  } else {
    const fx = resolveAttackFx(attackFx, characterId);
    const keys = [
      fx.originPreset,
      fx.flightPreset,
      fx.hitPreset,
      fx.takenHitPreset,
    ].filter(Boolean) as string[];
    const missing =
      presets == null
        ? []
        : keys.filter((k) => !presets.has(k));
    items.push({
      id: "attack_fx",
      severity: missing.length ? "fail" : "pass",
      detail: missing.length
        ? `missing presets: ${missing.join(", ")}`
        : `delivery=${fx.delivery} presets ok`,
    });

    if (
      (packId === "frontline" && characterId.startsWith("fl_")) ||
      (packId === "infinite-war" && characterId.startsWith("iw_"))
    ) {
      // Attack kit must be per-role; takenHit may stay shared victim FX.
      const attackKeys = [
        fx.originPreset,
        fx.flightPreset,
        fx.hitPreset,
      ].filter(Boolean) as string[];
      const sharedDf = attackKeys.filter((k) => k.startsWith("df."));
      items.push({
        id: "attack_fx_unique",
        severity: sharedDf.length ? "fail" : "pass",
        detail: sharedDf.length
          ? `must not reuse df.* presets: ${sharedDf.join(", ")}`
          : "pack-local kit only (no shared df.* attack presets)",
      });
    }

    const needsSpriteFxLayer =
      (packId === "frontline" && characterId.startsWith("fl_")) ||
      (packId === "infinite-war" && characterId.startsWith("iw_")) ||
      (packId === "portal-ffa" && FFA_COMBAT_QC.has(characterId));

    if (needsSpriteFxLayer) {
      // Separate FX sprite layer with affiliation rim (not body-baked smear).
      const sid = fx.spriteFxId;
      if (!sid) {
        items.push({
          id: "sprite_fx",
          severity: "fail",
          detail: "missing attackFx.spriteFxId",
        });
      } else {
        const kind = pack.manifest.spriteKinds?.[sid];
        const fxSprite = pack.sprites[sid];
        const fxAnim = pack.anims[sid];
        const idleLen = fxAnim?.dirs?.e?.idle?.frames?.length ?? 0;
        const solid = fxSprite ? solidCount(fxSprite.rows) : 0;
        const listed = pack.manifest.sprites.includes(sid);
        const ok =
          listed &&
          kind === "vfx" &&
          solid >= 12 &&
          idleLen >= 1 &&
          Boolean(fxAnim);
        items.push({
          id: "sprite_fx",
          severity: ok ? "pass" : "fail",
          detail: ok
            ? `${sid} kind=vfx solid=${solid} idleFrames=${idleLen}`
            : `need pack sprite kind=vfx + idle clip (got kind=${kind} solid=${solid} idle=${idleLen} listed=${listed})`,
        });

        const rimDiff = rimBakeDiff(pack, sid);
        items.push({
          id: "sprite_fx_rim",
          severity: rimDiff >= FX_RIM_DIFF_MIN ? "pass" : "fail",
          detail: `ally↔enemy rim bake diff=${rimDiff} (min ${FX_RIM_DIFF_MIN})`,
        });

        let minFxHam = Number.POSITIVE_INFINITY;
        let vsId = "";
        for (const otherId of Object.keys(attackFx.byRole)) {
          if (otherId === characterId) continue;
          if (packId === "frontline" && !otherId.startsWith("fl_")) continue;
          if (packId === "infinite-war" && !otherId.startsWith("iw_")) continue;
          if (packId === "portal-ffa" && !FFA_COMBAT_QC.has(otherId)) continue;
          const otherFx = resolveAttackFx(attackFx, otherId);
          const oid = otherFx.spriteFxId;
          if (!oid || oid === sid) continue;
          const otherRows = pack.sprites[oid]?.rows;
          if (!otherRows || !fxSprite) continue;
          const ham = silhouetteHamming(fxSprite.rows, otherRows);
          if (ham < minFxHam) {
            minFxHam = ham;
            vsId = oid;
          }
        }
        const distinctOk =
          minFxHam === Number.POSITIVE_INFINITY || minFxHam >= FX_DISTINCT_MIN;
        items.push({
          id: "sprite_fx_distinct",
          severity: distinctOk ? "pass" : "fail",
          detail:
            minFxHam === Number.POSITIVE_INFINITY
              ? "no peer FX to compare"
              : `min Hamming vs ${vsId}=${minFxHam} (min ${FX_DISTINCT_MIN})`,
        });

        // Body contact frames must not bake smear (accent or opaque) far outside idle.
        if (atkLens.e >= 2) {
          const frames = e.attack.frames;
          const c0 = Math.max(0, Math.floor(frames.length / 2) - 1);
          const c1 = Math.min(frames.length - 1, c0 + 1);
          const raw = opaqueBbox(eIdle);
          const pad = 5;
          const bbox = {
            minX: Math.max(0, raw.minX - pad),
            minY: Math.max(0, raw.minY - pad),
            maxX: Math.min(63, raw.maxX + pad),
            maxY: Math.min(63, raw.maxY + pad),
          };
          const smear0 = accentOutsideBbox(frames[c0]!, bbox);
          const smear1 = accentOutsideBbox(frames[c1]!, bbox);
          const smearMax = Math.max(smear0, smear1);
          const opaque0 = opaqueOutsideBbox(frames[c0]!, bbox);
          const opaque1 = opaqueOutsideBbox(frames[c1]!, bbox);
          const opaqueMax = Math.max(opaque0, opaque1);
          const accentOk = smearMax <= BODY_SMEAR_MAX;
          const opaqueOk = opaqueMax <= BODY_OPAQUE_SMEAR_MAX;
          items.push({
            id: "body_no_smear",
            severity: accentOk && opaqueOk ? "pass" : "fail",
            detail: `contact accentOutside=${smear0}/${smear1} (max ${BODY_SMEAR_MAX}); opaqueOutside=${opaque0}/${opaque1} (max ${BODY_OPAQUE_SMEAR_MAX}); smear on FX layer`,
          });
        }
      }
    }

    if (packId === "frontline" && atkLens.e >= 6) {
      const nAtk = atkLens.e;
      const contactMid = (Math.floor(nAtk / 2) - 0.5) / Math.max(1, nAtk - 1);
      const frac = fx.impactFrac ?? 0.35;
      const lo = contactMid - 0.2;
      const hi = contactMid + 0.2;
      const syncOk = frac >= lo && frac <= hi;
      items.push({
        id: "attack_impact_sync",
        severity: syncOk ? "pass" : "fail",
        detail: `impactFrac=${frac} vs contactMid≈${contactMid.toFixed(2)} [${lo.toFixed(2)},${hi.toFixed(2)}]`,
      });
    }
  }

  return {
    packId,
    characterId,
    overall: overallOf(items),
    items,
  };
}

export function formatCharacterQcReport(report: CharacterQcReport): string {
  const tag = report.overall.toUpperCase();
  const lines = [
    `OVERALL ${tag} · ${report.packId}/${report.characterId}`,
    ...report.items.map((it) => {
      const s =
        it.severity === "pass" ? "OK" : it.severity === "warn" ? "WARN" : "FAIL";
      return `${s} ${it.id}: ${it.detail}`;
    }),
  ];
  return lines.join("\n");
}

export function qcBadge(overall: QcSeverity): string {
  if (overall === "pass") return "[QC:PASS]";
  if (overall === "warn") return "[QC:WARN]";
  return "[QC:FAIL]";
}

export function qcColor(overall: QcSeverity): string {
  if (overall === "pass") return "#7dffb0";
  if (overall === "warn") return "#ffd54f";
  return "#ff8a80";
}
